/**
 * Kelly's right-side dock: one `position: fixed` box mounted once at `AccountRoot`, hosting the existing
 * `ConversationSurface` beside whatever page the user is on.
 *
 * Three things about it are load-bearing and easy to undo by accident:
 *  - `closed` is an off-screen transform plus `visibility: hidden`, never `display: none`. With `display: none`
 *    every `getBoundingClientRect()` returns zeros, `use-transcript-scroll.ts` writes a 0px composer clearance
 *    and the transcript's `scrollTop` is discarded — the dock comes back broken.
 *  - The node is never conditionally rendered or reparented while Kelly is available: one `<Box>`, several `sx`
 *    objects. Reparenting is a remount, and expand/minimise preserving the conversation, the artifacts and the
 *    scroll position is a property of that node surviving rather than of anything saving and restoring state.
 *  - The reservation custom property is stepped in a single frame and never transitioned. A width transition
 *    fires ~11 ResizeObserver callbacks and dockview's internal observer is not rAF-coalesced. The dock's own
 *    `transform` slides; the page snaps.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import type { KeyboardEvent } from "react"
import { useNavigate } from "react-router-dom"
import { Box, styled } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { useAppDispatch, useAppSelector, useAppStore } from "hooks/redux"
import { useAccountAlias } from "resources/account"
import { useScheduledWorkflowsCount } from "resources/scheduled-workflows/hooks"
import { AI_TEAMMATE_EVENTS, PANEL_SURFACES, THREAD_LIST_SURFACES } from "connected-components/ai-teammate/analytics/events"
import { useAnalytics } from "connected-components/analytics"
import { closeSkillsPane, openSkillsPane, selectDockPane, selectDockView, selectDockWidthPx, selectHasActivated, selectSurfaceView, setDockView, setDockWidth, setReportToOpen, setSurfaceView } from "../store/agent-chat-slice"
import { ChatLayoutProvider } from "./chat-layout-context"
import type { ChatLayoutValue } from "./chat-layout-context"
import { ConversationSurface } from "./conversation-surface"
import { ConversationsPreview } from "./conversations-preview"
import { DockHeader } from "./dock-header"
import type { ConversationsTriggerProps } from "./dock-header"
import { artifactDockWidth } from "../geometry/dock-artifact-width"
import { DOCK_WIDTH_VAR, STAGE_LEFT_VAR, clampDockWidth, dockWidthCss } from "../geometry/dock-metrics"
import { isUnclaimedEscape } from "./escape-key"
import { useKellyChat, useKellyChatEntitlement, useKellyChatOptional } from "./kelly-chat-context"
import { ReportsSection } from "../scheduled-workflows/reports-section"
import { ChatSidePanel } from "./side-panel"
import type { ThreadRowCallbacks } from "./thread-list-item"
import { useEdgeResize } from "./use-edge-resize"
import { useLatest } from "./use-latest"
import { useHoverPreview } from "./use-hover-preview"
import { useFocusDockOpener } from "./dock-opener"

/**
 * Below the legacy modal backdrop (1110) and MUI's `Modal` (1300), above every piece of in-page chrome —
 * including the cursor-following chart tooltip at 1102. That tooltip's edge-flip needs the cursor within 300px
 * of the viewport's right edge, and the dock's 320px FLOOR already exceeds that band, so a chart under a docked
 * Kelly can never reach it at ANY dock width. A modal is modal: it should dim and cover the dock.
 */
const DOCK_Z_INDEX = 1105
const SLIDE_MS = 180

const DOCK_SX = {
  position: "fixed",
  top: 0,
  right: 0,
  bottom: 0,
  zIndex: DOCK_Z_INDEX,
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  bgcolor: "var(--app-bg-paper)",
  borderLeft: 1,
  borderColor: "divider",
  boxShadow: 8,
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
} satisfies SxProps<Theme>
const DOCK_OPEN_SX = { ...DOCK_SX, transform: "translateX(0)", visibility: "visible", transition: `transform ${SLIDE_MS}ms ease` } satisfies SxProps<Theme>
// visibility flips only once the slide has finished, so the dock is watchable on the way out and inert once gone.
const DOCK_CLOSED_SX = { ...DOCK_SX, transform: "translateX(100%)", visibility: "hidden", transition: `transform ${SLIDE_MS}ms ease, visibility 0s linear ${SLIDE_MS}ms` } satisfies SxProps<Theme>
/**
 * Full screen covers the STAGE, not the viewport: the nav rail stays visible, so the active section is still
 * legible and there is an exit that is not inside Kelly. Stepped rather than transitioned, for a stronger
 * version of the reservation's reason — an animated width reflows the transcript AND re-renders every Plotly
 * artifact in it, once per frame.
 */
const DOCK_FULL_SX = { ...DOCK_SX, left: `var(${STAGE_LEFT_VAR}, 0px)`, transform: "translateX(0)", visibility: "visible", transition: "none", boxShadow: 0 } satisfies SxProps<Theme>

const BODY_SX = { position: "relative", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" } satisfies SxProps<Theme>
// Carries `inert` while the slide-over is up, so the composer underneath a modal-looking scrim is not tabbable.
// Rendered UNCONDITIONALLY: a conditional wrapper reparents the surface, and this dock's whole architecture is
// that node never being remounted.
const SURFACE_SX = { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" } satisfies SxProps<Theme>
// The conversations slide-over stays INSIDE the dock: the prototype's page-wide scrim covers the dashboard the
// user docked Kelly beside in order to watch, and at any dock narrower than the page it has nowhere to fly out to.
const FLYOUT_SX = { position: "absolute", inset: 0, zIndex: 2, display: "flex" } satisfies SxProps<Theme>
const SCRIM_SX = { flex: 1, bgcolor: "rgba(0, 0, 0, 0.32)" } satisfies SxProps<Theme>

// Left-edge grab strip, mirroring the artifact panel's. Mouse-only and not focusable, so it never traps keyboard users.
const ResizeHandle = styled("div")(({ theme }) => ({
  position: "absolute",
  top: 0,
  bottom: 0,
  left: 0,
  width: theme.spacing(0.75),
  cursor: "col-resize",
  zIndex: 3,
  touchAction: "none",
  "&:hover": { backgroundColor: theme.palette.action.hover },
}))

/** The conversations list as an in-dock slide-over. `ChatSidePanel` verbatim; its own Collapse control closes it. */
function DockConversationsFlyout({ onClose, isSkillsOpen }: { onClose: () => void, isSkillsOpen: boolean }) {
  const dispatch = useAppDispatch()
  const analytics = useAnalytics()
  const surfaceView = useAppSelector(selectSurfaceView)
  const {
    threads, activeThreadId, canPinThreads, isThreadsLoaded, isThreadsErrored, mutatingThreadId,
    selectThread, renameThread, pinThread, deleteThread, startNewThread,
  } = useKellyChat()

  const callbacks = useMemo<ThreadRowCallbacks>(
    () => ({
      onSelect: (threadId: string) => { onClose(); dispatch(closeSkillsPane()); selectThread(threadId) },
      onRename: renameThread,
      onPin: pinThread,
      onDelete: deleteThread,
    }),
    [onClose, dispatch, selectThread, renameThread, pinThread, deleteThread],
  )
  const onStartNewThread = useCallback(() => { onClose(); dispatch(closeSkillsPane()); startNewThread() }, [onClose, dispatch, startNewThread])
  const skills = useMemo(
    () => ({ isOpen: isSkillsOpen, onOpen: () => { onClose(); dispatch(openSkillsPane()) } }),
    [isSkillsOpen, onClose, dispatch],
  )

  const onOpenReport = useCallback((deliverableId: string) => {
    onClose()
    dispatch(closeSkillsPane())
    dispatch(setReportToOpen(deliverableId))
  }, [onClose, dispatch])

  const { isEnabled: isWorkflowsEnabled, count: workflowsCount } = useScheduledWorkflowsCount()

  // The flyout closes on every navigation, the same as picking a conversation — it is a slide-over over
  // the body it just switched.
  const surfaceNav = useMemo(() => ({
    isArtifactsActive: surfaceView === "artifacts",
    onOpenArtifacts: () => {
      analytics.track(AI_TEAMMATE_EVENTS.ARTIFACTS_SURFACE_OPENED, PANEL_SURFACES.DOCK_FLYOUT)
      onClose()
      dispatch(closeSkillsPane())
      dispatch(setSurfaceView("artifacts"))
    },
    workflows: isWorkflowsEnabled
      ? {
        isActive: surfaceView === "workflows",
        onOpen: () => {
          onClose()
          dispatch(closeSkillsPane())
          dispatch(setSurfaceView("workflows"))
        },
        count: workflowsCount,
      }
      : undefined,
    onShowConversation: () => dispatch(setSurfaceView("conversation")),
  }), [surfaceView, analytics, onClose, dispatch, isWorkflowsEnabled, workflowsCount])

  return (
    <Box sx={FLYOUT_SX} data-testid="kelly-dock-flyout">
      <ChatSidePanel
        threads={threads}
        activeThreadId={activeThreadId}
        canPinThreads={canPinThreads}
        surface={THREAD_LIST_SURFACES.DOCK_FLYOUT}
        isThreadsLoaded={isThreadsLoaded}
        isThreadsErrored={isThreadsErrored}
        mutatingThreadId={mutatingThreadId}
        callbacks={callbacks}
        onStartNewThread={onStartNewThread}
        onCollapse={onClose}
        surfaceNav={surfaceNav}
        skills={skills}
        reports={isWorkflowsEnabled ? <ReportsSection onOpen={onOpenReport} /> : undefined}
      />
      <Box sx={SCRIM_SX} onClick={onClose} data-testid="kelly-dock-flyout-scrim" />
    </Box>
  )
}

export function KellyDock() {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  // Null means the gate's boundary caught a Kelly crash and is rendering the app bare, or this is a non-app
  // section. Either way the dock goes with it — and the reservation cleanup below is what un-indents the app.
  const controller = useKellyChatOptional()
  // Entitlement, never a non-null controller: the gate mounts an idle one for every account on every app page.
  const entitlement = useKellyChatEntitlement()
  const dockView = useAppSelector(selectDockView)
  const width = useAppSelector(selectDockWidthPx)
  const hasActivated = useAppSelector(selectHasActivated)
  const isSkillsOpen = useAppSelector(selectDockPane) === "skills"
  const closeSkillsPage = useCallback(() => dispatch(closeSkillsPane()), [dispatch])
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false)
  // The ☰ previews the list on hover and opens it on click — the same peek the collapsed rail's + button opens,
  // at the same width. Anchor is state, not a ref: reading a ref during render is forbidden here.
  const [conversationsAnchorEl, setConversationsAnchorEl] = useState<HTMLButtonElement | null>(null)
  const { isOpen: isPreviewOpen, surfaceRef: previewRef, closeNow: closePreview, triggerHandlers: previewTriggerHandlers, surfaceHandlers: previewSurfaceHandlers } = useHoverPreview()
  // The dock header's ☰ peek is the same affordance the collapsed rail's + opens, and reports the same event.
  // Only closed -> open: useHoverPreview re-fires `open` when the pointer returns during the close delay.
  const wasPreviewOpenRef = useRef(isPreviewOpen)
  const focusOpener = useFocusDockOpener()
  const analytics = useAnalytics()

  useEffect(() => {
    if (isPreviewOpen && !wasPreviewOpenRef.current) analytics.track(AI_TEAMMATE_EVENTS.RECENTS_PREVIEW_OPENED, { surface: "dock_header" })
    wasPreviewOpenRef.current = isPreviewOpen
  }, [isPreviewOpen, analytics])

  const isAvailable = controller != null && entitlement === "confirmed"
  const isFull = dockView === "full"
  const isOpen = dockView === "docked" || isFull
  const widthCss = dockWidthCss(width)

  // A closed dock has no flyout — whichever control closed it. `onRequestClose` is not the only close path: the
  // launcher pill and `/kelly-ai`'s restore dispatch the view straight to the slice, and a flyout left open would
  // be waiting behind the next open. Adjusted during render (React's own pattern) rather than in an effect, which
  // would paint the stale flyout for a frame on the way out.
  const [wasOpen, setWasOpen] = useState(isOpen)
  if (wasOpen !== isOpen) {
    setWasOpen(isOpen)
    if (!isOpen) setIsFlyoutOpen(false)
  }

  const closeFlyout = useCallback(() => setIsFlyoutOpen(false), [])
  // The header sits above the slide-over, so its + stays clickable while the list is open — and a new chat
  // started behind the scrim is a conversation the user cannot see. Every sibling path dismisses first.
  const onNewChat = useCallback(() => {
    closePreview()
    setIsFlyoutOpen(false)
    dispatch(closeSkillsPane())
    dispatch(setSurfaceView("conversation"))
    controller?.startNewThread()
  }, [closePreview, dispatch, controller])
  // Reuses the page panel's own event names with a `surface`, rather than minting dock-specific ones: it is the
  // same conversations list being shown and hidden, and one series answers "do people use the list" for both.
  // Reported off the open/closed TRANSITION, not from the header toggle: the scrim, the panel's own collapse
  // control, Escape, picking a thread, starting a new chat and the dock closing all hide the flyout without
  // passing through `toggleFlyout`, and counting only the toggle left expands and collapses unbalanced.
  const wasFlyoutOpenRef = useRef(isFlyoutOpen)
  useEffect(() => {
    if (wasFlyoutOpenRef.current === isFlyoutOpen) return
    wasFlyoutOpenRef.current = isFlyoutOpen
    analytics.track(isFlyoutOpen ? AI_TEAMMATE_EVENTS.PANEL_EXPANDED : AI_TEAMMATE_EVENTS.PANEL_COLLAPSED, PANEL_SURFACES.DOCK_FLYOUT)
  }, [isFlyoutOpen, analytics])
  const toggleFlyout = useCallback(() => {
    closePreview()
    setIsFlyoutOpen((open) => !open)
  }, [closePreview])
  const onRequestClose = useCallback(() => {
    closePreview()
    setIsFlyoutOpen(false)
    dispatch(setDockView("closed"))
    // Before React applies `inert`, so focus leaves under its own steam rather than being dropped to the body.
    focusOpener()
  }, [closePreview, dispatch, focusOpener])
  // Promoting seats the real drawer, so the flyout standing in for it goes with the view it belonged to.
  // Full screen IS the `/kelly-ai` route: Expand navigates there and the route bridge promotes the
  // dock, so the URL, the nav rail and browser Back all agree about where the user is. Minimise flips the view
  // here and the bridge leaves the route on its own.
  const navigate = useNavigate()
  const accountAlias = useAccountAlias()
  const onToggleFull = useCallback(() => {
    closePreview()
    setIsFlyoutOpen(false)
    if (isFull) dispatch(setDockView("docked"))
    else navigate(`/${accountAlias}/kelly-ai`)
  }, [closePreview, dispatch, isFull, navigate, accountAlias])
  // While the full list is open the peek has nothing to preview. Unbinding hover is the ONE mechanism that keeps
  // the two apart — a second `open` guard on the Popper would be a duplicate that has to agree with this one.
  const conversationsTriggerProps = useMemo<ConversationsTriggerProps>(
    () => (isFlyoutOpen ? { ref: setConversationsAnchorEl } : { ref: setConversationsAnchorEl, ...previewTriggerHandlers }),
    [isFlyoutOpen, previewTriggerHandlers],
  )

  /**
   * The reservation has three writers — the effect below, the drag's rAF and its commit — and only the effect
   * runs at a moment React controls. `visibility: hidden` does NOT release pointer capture (only removing the
   * element does, and the dock is deliberately never removed), so an Escape mid-gesture closes the dock while
   * the drag keeps writing. A write that lands after the effect's cleanup is permanent: the effect is already
   * in its early-return branch and registers no cleanup to undo it, and the app stays indented with no dock in
   * it. So the out-of-band writers are latched off the instant the dock stops reserving.
   */
  // `docked`, never merely open: in `full` the dock overlays and the page reserves NOTHING. Collapsing the two
  // width concepts into one is what would make expand/minimise reflow the whole page underneath, twice.
  const isReserving = isAvailable && dockView === "docked"
  const isReservingRef = useRef(false)
  const writeReservationIfOpen = useCallback((value: string) => {
    if (isReservingRef.current) document.documentElement.style.setProperty(DOCK_WIDTH_VAR, value)
  }, [])

  const commitWidth = useCallback((next: number) => {
    // Written here as well as in the effect below: a drag that ends on the width it started from changes no
    // state, so the effect would not re-run and the raw px from the last frame would keep the CSS bound off.
    writeReservationIfOpen(dockWidthCss(next))
    dispatch(setDockWidth(next))
  }, [dispatch, writeReservationIfOpen])

  /**
   * Artifact-driven width. An artifact opening into a dock too narrow to split it asks for the
   * threshold width and gives it back on close — but ONLY what this owned, and only while it still owns it.
   * The width to hand back, or null when nothing here owns the width. This adds no writer to the reservation
   * custom property: it goes through `commitWidth`, which is already latched off when the dock stops reserving.
   */
  const restoreWidthRef = useRef<number | null>(null)
  const hasArtifactRef = useRef(false)
  // Ownership is a SECOND fact, with a different lifetime from the restore value: "nothing to hand back" and
  // "the user took this width off us" both read as a null restore, and the next widen trigger then re-takes a
  // width they deliberately gave up. Scoped to the open artifact — a drag with none up is just their width, and
  // the next artifact may still widen from it (and hand it straight back).
  const hasUserWidthRef = useRef(false)
  // A width the user commits by hand is theirs from then on — so the artifact closing hands nothing back and
  // can never shrink a dock they widened deliberately. Every gesture exit routes through here (settleDrag).
  // Through a ref, not a dependency: `analytics` carries `route`, so it changes identity on every in-account
  // navigation — and this callback is what `useEdgeResize` derives `settleDrag` from, whose identity the hook
  // documents as having to stay stable because a layout effect binds it.
  const analyticsRef = useLatest(analytics)
  const commitDragWidth = useCallback((next: number) => {
    if (hasArtifactRef.current) hasUserWidthRef.current = true
    restoreWidthRef.current = null
    commitWidth(next)
    // Only the gesture reports: the artifact-driven widen and its hand-back go through commitWidth directly,
    // and counting those as user resizes would make the dock look hand-tuned far more often than it is.
    analyticsRef.current.track(AI_TEAMMATE_EVENTS.DOCK_RESIZED, { widthPx: next })
  }, [commitWidth, analyticsRef])

  /**
   * The widen itself, applied from either place it can fall due: an artifact opening into a reserving dock, or a
   * dock that STARTS reserving with one already open — minimising out of full screen. The presence report cannot
   * cover the second: the surface reports on an artifact change and its callback's identity never varies, so a
   * view flip re-runs nothing. (Adding the view to the region's effect deps would instead report a spurious
   * presence-false on every flip and churn the width through a shrink and a re-widen.)
   */
  const widenForArtifact = useCallback(() => {
    if (hasUserWidthRef.current || restoreWidthRef.current != null) return
    // Fresh off the store, never a render closure: this runs from the surface's effect, one commit behind.
    const current = store.getState().agentChat.dockWidthPx
    const target = artifactDockWidth(current, window.innerWidth)
    if (target == null) return
    restoreWidthRef.current = current
    commitWidth(target)
  }, [commitWidth, store])

  const onArtifactPresence = useCallback((hasArtifact: boolean) => {
    hasArtifactRef.current = hasArtifact
    if (!hasArtifact) {
      const restore = restoreWidthRef.current
      restoreWidthRef.current = null
      hasUserWidthRef.current = false
      if (restore != null) commitWidth(restore)
      return
    }
    // Only a `docked` dock reserves page width; `full` sizes off the stage and has area to spare either way.
    if (isReservingRef.current) widenForArtifact()
  }, [commitWidth, widenForArtifact])

  const layout = useMemo<ChatLayoutValue>(
    () => ({ mode: "dock", view: dockView, onRequestClose, onToggleFull, onArtifactPresence }),
    [dockView, onRequestClose, onToggleFull, onArtifactPresence],
  )

  const captureClamp = useCallback(() => {
    const viewportWidth = window.innerWidth
    return (candidate: number) => clampDockWidth(candidate, viewportWidth)
  }, [])
  // The page has to follow the drag frame-for-frame, or content sits under the dock until the pointer is released.
  const onDragFrame = useCallback((next: number) => {
    writeReservationIfOpen(`${next}px`)
  }, [writeReservationIfOpen])
  // What the `style` prop below resolves to for a committed width, so the settle can hand the element back to the
  // CSS clamp. Read off the store rather than the render closure, which keeps this — and `settleDrag` — stable.
  const widthStyle = useCallback(
    (next: number) => (store.getState().agentChat.dockView === "full" ? "auto" : dockWidthCss(next)),
    [store],
  )
  const { elementRef, resizeHandleProps, settleDrag } = useEdgeResize({ width, onWidthChange: commitDragWidth, captureClamp, widthStyle, onDragFrame })

  // Ending the gesture here rather than in each close path covers every one of them. Declaration order is NOT
  // load-bearing: React destroys every layout effect for a fiber before it creates any, so the property's
  // cleanup precedes this body either way, and the latch is assigned before settleDrag runs within it.
  // The cleanup is: on unmount the hook's rAF cancel is a PASSIVE effect, so a queued frame can still fire and
  // reach onDragFrame with no dock left to reserve for.
  // Its own ref, not `isReservingRef`, which the cleanup below zeroes before every re-run: the transition is what
  // makes the widen fire once on minimise rather than re-imposing itself on every re-render.
  const wasReservingRef = useRef(false)
  useLayoutEffect(() => {
    isReservingRef.current = isReserving
    if (!isReserving) settleDrag()
    else if (!wasReservingRef.current && hasArtifactRef.current) widenForArtifact()
    wasReservingRef.current = isReserving
    return () => { isReservingRef.current = false }
  }, [isReserving, settleDrag, widenForArtifact])

  // The reservation `.modules_account-root` pads itself by. ALWAYS removed on the way out — a stuck property
  // leaves the whole app permanently indented with no dock in sight, which is this architecture's worst failure.
  useLayoutEffect(() => {
    if (!isReserving) return undefined
    const root = document.documentElement
    root.style.setProperty(DOCK_WIDTH_VAR, widthCss)
    return () => { root.style.removeProperty(DOCK_WIDTH_VAR) }
  }, [isReserving, widthCss])

  // One bubble-phase Escape ladder. Search-clear stops propagation and the artifact REGION (not just the panel,
  // which is focus-scoped) calls preventDefault, so an Escape reaching here has been claimed by neither.
  const onKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (!isUnclaimedEscape(event)) return
    event.preventDefault()
    if (isFlyoutOpen) {
      setIsFlyoutOpen(false)
      return
    }
    if (isSkillsOpen) {
      closeSkillsPage()
      return
    }
    onRequestClose()
  }, [isFlyoutOpen, isSkillsOpen, closeSkillsPage, onRequestClose])

  if (!isAvailable) return null

  // Non-modal by construction: an <aside>, no FocusTrap, no role="dialog", no aria-modal —
  // the page beside the dock stays readable and operable.
  return (
    <Box
      component="aside"
      aria-label="Kelly"
      data-testid="kelly-dock"
      ref={elementRef}
      // Non-modal is about no focus TRAP, not no focus management: without this the header buttons stay
      // tabbable off-screen for the 180ms the visibility flip is delayed by, and a focused control inside a
      // closing dock keeps focus on an invisible element. `inert` also hands focus back to the document.
      inert={!isOpen}
      sx={isFull ? DOCK_FULL_SX : isOpen ? DOCK_OPEN_SX : DOCK_CLOSED_SX}
      // `auto` rather than dropping the property: the drag writes this element's width imperatively, so the
      // value React is reconciling against has to keep changing for it to be rewritten on the way back.
      style={{ width: isFull ? "auto" : widthCss }}
      onKeyDown={onKeyDown}
    >
      {/* Nothing to drag when the dock is pinned between the rail and the viewport edge. */}
      {!isFull && <ResizeHandle {...resizeHandleProps} data-testid="kelly-dock-resize-handle" />}
      <DockHeader
        isConversationsOpen={isFlyoutOpen}
        isFull={isFull}
        conversationsTriggerProps={conversationsTriggerProps}
        onToggleConversations={toggleFlyout}
        onNewChat={onNewChat}
        onToggleFull={onToggleFull}
        onClose={onRequestClose}
      />
      {/* Portalled to document.body, so it escapes the dock's transform and its 1105 — which is exactly what the
          ☰ needed: in `docked` view an open artifact overlays the whole dock body. */}
      <ConversationsPreview
        isOpen={isPreviewOpen}
        anchorEl={conversationsAnchorEl}
        surfaceRef={previewRef}
        surfaceHandlers={previewSurfaceHandlers}
        onDismiss={closePreview}
        testId="kelly-dock-conversations-preview"
      />
      <Box sx={BODY_SX}>
        <Box sx={SURFACE_SX} inert={isFlyoutOpen} data-testid="kelly-dock-surface">
          <ChatLayoutProvider value={layout}>
            {hasActivated && <ConversationSurface />}
          </ChatLayoutProvider>
        </Box>
        {isFlyoutOpen && <DockConversationsFlyout onClose={closeFlyout} isSkillsOpen={isSkillsOpen} />}
      </Box>
    </Box>
  )
}
